import { useEffect, useState } from "react";

/**
 * Публичные ставки платформы для страниц, доступных до входа.
 *
 * Авторизованному пользователю ставки приходят в ответе add-user.php и лежат
 * в userData.settings. Лендинг KredaFi открыт всем, add-user.php там ещё не
 * вызывался — поэтому цифры на нём брались из кода и не менялись, что бы
 * администратор ни выставил в панели. Хук ходит за теми же значениями в
 * публичный эндпоинт /api/rates-public.php.
 *
 * Ошибка сети или недоступный эндпоинт — не повод показать пустоту:
 * возвращаем null, вызывающий код падает на свои значения по умолчанию.
 */
export interface PublicRates {
  max_rate_overnight?: number;
  max_rate_overnight_high_risk?: number;
  demand_rate_overnight?: number;
  marginal_rate_overnight?: number;
  margin_rate?: number;
  max_rate?: number;
  max_rate_high_risk?: number;
}

export function usePublicRates(): PublicRates | null {
  const [rates, setRates] = useState<PublicRates | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetch("/api/rates-public.php", { credentials: "omit" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data || !data.rates) return;
        const raw = data.rates as Record<string, unknown>;
        const parsed: PublicRates = {};
        (Object.keys(raw) as (keyof PublicRates)[]).forEach((key) => {
          const value = Number(raw[key as string]);
          if (Number.isFinite(value)) parsed[key] = value;
        });
        setRates(parsed);
      })
      .catch(() => {
        // Тихо: лендинг должен открыться и без API.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return rates;
}

/** Число из ставок с запасным значением, если API недоступен или ставка не задана. */
export function rateOr(
  rates: PublicRates | null,
  key: keyof PublicRates,
  fallback: number
): number {
  const value = rates?.[key];
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : fallback;
}

/**
 * До востребования всегда чуть выше ночи. Если в админке пара перевёрнута
 * или ставка пуста — не показываем «flexible дешевле overnight» как у Gemini.
 */
export function demandRateOr(
  rates: PublicRates | null,
  overnight: number
): number {
  const demand = rateOr(rates, "demand_rate_overnight", 0);
  if (demand > overnight) return demand;
  return Math.round(overnight * 108) / 100;
}
